import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigCacheService } from '@services/api/config-cache.service';

/**
 * Session cache for the Pattern-A endpoints left "not done" in
 * docs/backend/duplicate-api-calls-frontend-response.md: office/user config data that was
 * re-fetched, byte-identical, on every module mount (tag-settings/me/offices-my already have
 * their own fix via `StateCacheFetchService` and are deliberately not listed here).
 *
 * A true `HttpInterceptor` rather than a change inside `ApiService`, because at least one call
 * site (`leads.service.ts`) calls `HttpClient` directly and never goes through `ApiService.get()`.
 *
 * Invalidation is deliberately NOT done here by sniffing write URLs — each write path (project
 * create/rename, staff changes, task-status admin, preferences save, library upload) calls
 * `ConfigCacheService.evict()` itself, at the point the data actually changes, the same way
 * `StateCacheFetchService`'s tag-settings window is invalidated from `setting.service.ts`.
 */
@Injectable()
export class ConfigCacheInterceptor implements HttpInterceptor {
  private static readonly CACHEABLE_GET_FRAGMENTS: string[] = [
    '/library/get-office-library/project-extract-sale-files',
    '/users/byoffice',
    '/users/office-staff-list',
    '/projects-name-list',
    '/listing/all-suburbs',
    '/task-statuses',
    '/users/display-preferences',
  ];

  constructor(private configCache: ConfigCacheService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.method !== 'GET' || !this.isCacheable(req.urlWithParams)) {
      return next.handle(req);
    }

    const cached = this.configCache.get(req.urlWithParams);
    if (cached) {
      return of(cached.clone());
    }

    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse && event.ok) {
          this.configCache.set(req.urlWithParams, event.clone());
        }
      })
    );
  }

  private isCacheable(url: string): boolean {
    return ConfigCacheInterceptor.CACHEABLE_GET_FRAGMENTS.some(fragment => url.includes(fragment));
  }
}
